/**
 * Ported VERBATIM from `apr_template/src/pages/ai-analysis/LicensingAnalysis.tsx`.
 * Only import paths change. See ../compat/ for the shims.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import apiService from '../compat/api';
import HelpPanel from '../compat/HelpPanel';
import PageHeader from '../compat/PageHeader';
import type { Help } from '../compat/helpTypes';
import { useAuth } from '../compat/AuthContext';

// ── helpers ──────────────────────────────────────────────────────────────────
const money = (n: any) => `$${Math.round(Number(n) || 0).toLocaleString('en-US')}`;
const pctOf = (part: any, whole: any) => { const w = Number(whole) || 0; return w > 0 ? `${Math.round((Number(part) || 0) / w * 100)}%` : '—'; };

// ── Vendor-family composition ───────────────────────────────────────────────
// Families are grouped by VENDOR, so one family can span several parent trees plus
// standalone applications (Microsoft = the M365 tree + the Azure tree + 8 standalones).
// The server reports the real split in parent_groups / standalone_count.

/** True when every member really does sit under the one parent shown in the heading —
 *  the only case where displaying that parent's code for the whole group is honest. */
const isSingleParentFamily = (f: any): boolean =>
  !!f.parent_application_id_code
  && (f.parent_groups?.length ?? 0) <= 1
  && (f.standalone_count ?? 0) === 0;

/** " · 2 families + 8 standalone" — omitted when the group is a single clean tree. */
const familySplitLabel = (f: any): string => {
  const groups = f.parent_groups?.length ?? 0;
  const solo = f.standalone_count ?? 0;
  if (isSingleParentFamily(f) || (groups <= 1 && solo === 0)) return '';
  const parts: string[] = [];
  if (groups) parts.push(`${groups} ${groups === 1 ? 'family' : 'families'}`);
  if (solo) parts.push(`${solo} standalone`);
  return parts.length ? ` · ${parts.join(' + ')}` : '';
};

/** "APP-0446 Microsoft 365 (10) · APP-0455 Microsoft Azure (3) · 8 standalone" */
const familyCompositionTitle = (f: any): string => {
  const parts = (f.parent_groups || []).map(
    (g: any) => `${g.application_id_code || '—'} ${g.name || ''} (${g.count})`.trim(),
  );
  if (f.standalone_count) parts.push(`${f.standalone_count} standalone`);
  return parts.length ? `Grouped by vendor. Contains: ${parts.join(' · ')}` : '';
};

const DISPO_COLOR: Record<string, string> = {
  'RENEGOTIATE': '#2563eb', 'RETAIN/RENEGOTIATE': '#0891b2', 'RETAIN STRATEGIC': '#6b7280',
  'CONSOLIDATE': '#7c3aed', 'REPLACE': '#dc2626', 'EXCLUDED/IN FLIGHT': '#9ca3af',
};
const Badge: React.FC<{ text?: string; color?: string }> = ({ text, color = '#6b7280' }) => (
  <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700, color: '#fff', background: color, whiteSpace: 'nowrap' }}>{text || '—'}</span>
);
const RiskBadge: React.FC<{ level?: string }> = ({ level }) => {
  const c = level === 'Low' ? '#16a34a' : level === 'High' ? '#dc2626' : '#d97706';
  return <span style={{ fontSize: 11, fontWeight: 600, color: c }}>{level || '—'}</span>;
};
const card: React.CSSProperties = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16 };
// Tree controls — same neutral grey outline as the Licensing Results screen, so the two
// screens read as one system.
const btnTree: React.CSSProperties = { height: 30, padding: '0 10px', background: '#fff', color: '#546e7a', border: '1px solid #cfd8dc', borderRadius: 7, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' };
/** How far a child row is inset from its parent. Drives the checkbox AND the App ID cell so
 *  the tick sits beside the code it belongs to rather than under the parent's checkbox. */
const CHILD_INDENT = 30;

// "How this works" — uses the shared HelpPanel design (same as every other screen).
const LicensingAnalysisHelp: Help = {
  title: 'Licensing Analysis',
  icon: '💷',
  purpose:
    'Runs a relicensing-and-savings analysis across the applications you flag for licence negotiation, and turns it into a negotiation-ready plan — per application and per vendor family (Microsoft, Salesforce, SAP, Oracle, Workday …). The analysis is <b>driven by the vendor negotiation prompt (playbook) plus the customer\'s contract</b>: together they govern the methodology, taxonomy and savings tiers. The engine enforces the arithmetic and keeps a Low / Mid / High saving on every app; Claude supplies the judgement (disposition, successor, levers, rationale), grounded in each app\'s data.',
  doHere: [
    'The screen opens as a <b>raw list</b> of eligible apps grouped by vendor/partner — no analysis runs automatically.',
    'Attach the customer\'s <b>licensing document</b> (contract, order form, price list) to the app <i>or its vendor parent</i> in the Documents panel — a document on any Microsoft app (e.g. the M365 parent) is shared across the whole Microsoft family.',
    'Pick the <b>vendor negotiation prompt</b> that governs the run, or generate a <b>New Prompt</b> for a vendor you don\'t have yet — the prompt and the contract, not fixed bands, decide the taxonomy and savings tiers.',
    '<b>Tick</b> a single app, a whole partner group, or <b>Select all</b>, then press <b>Run analysis</b> — you only analyse what you select.',
    'Every run is versioned automatically. On <b>Licensing Results</b>, select the apps (and versions) you want and generate dated, per-application <b>Word</b> and <b>PowerPoint</b> negotiation packs.',
  ],
  fits: 'License Intelligence &rarr; Licensing Analysis',
  firstTime: 'Attach the licensing document to the vendor parent, tick that partner group, then press Run analysis.',
  sections: [
    {
      type: 'defs',
      label: 'Default dispositions & savings bands (% of current cost)',
      items: [
        { term: 'RENEGOTIATE', color: '#1e3a8a', def: '<b>10–20%</b> — keep the app; cut cost at renewal with a credible competitive alternative in play. FinOps / consumption lines (e.g. Azure) run 20–30%.' },
        { term: 'REPLACE', color: '#7f1d1d', def: '<b>25–55%</b> — swap to a structurally cheaper modern alternative; ~6–18 month migration.' },
        { term: 'CONSOLIDATE', color: '#4c1d95', def: '<b>30–80%</b> — eliminate the vendor by absorbing its function into an already-paid platform.' },
        { term: 'RETAIN STRATEGIC', color: '#202124', def: '<b>5–10%</b> — strategic / embedded, minimal leverage; benchmark renewal pressure only.' },
        { term: 'EXCLUDED/IN FLIGHT', color: '#202124', def: '<b>0%</b> — out of scope, or savings already captured by an active project. Shown, not counted.' },
      ],
      note:
        'These are the <b>fallback</b> taxonomy and bands — used when the chosen negotiation prompt and the contract don\'t define their own. Where a vendor playbook or the contract dictates a different disposition set or savings range, <b>those win</b>. Either way each app gets one disposition anchoring a Low / <b style="color:#16a34a">Mid</b> / High band; the <b>Mid</b> value is the headline and the basis for the <b>proposed new licence cost = current − Mid savings</b> (the biggest saving gives the lowest price).',
    },
    {
      type: 'html',
      label: 'Estimate vs. contract-grade',
      html:
        'If the customer has <b>not provided licensing information</b> for a selected app (no document on it or its parent), its numbers are <b>industry-benchmark estimates</b>, flagged with ⚠ on the row and in the app detail. Attach a document and re-run to move from estimate to contract-grounded.',
    },
    {
      type: 'html',
      label: 'One-time cost & timing',
      html:
        'Renegotiate / retain plays carry <b>$0</b> one-time cost. Replace / consolidate add a one-time cost = exit + successor integration (benchmark rates). Savings are <b>steady-state (year-2+)</b> and are <b>never netted</b> from the one-time cost; year-1 realisation is partial (~40–60% replacement, ~70–90% renegotiation).',
    },
    {
      type: 'html',
      label: 'Vendor families (parent → children)',
      html:
        'Spend is analysed and negotiated at the <b>family</b> level, because combined volume is the leverage. A parent carries $0 when its dollars are dispersed to children; bundled children (e.g. SharePoint inside M365 E5) carry $0 marginal. Each family gets a single master <b>strategy, sequencing and ask</b> — the “Group playbook”.',
    },
    {
      type: 'html',
      label: 'What you get per application',
      html:
        'A disposition, the Low/Mid/High band and proposed new cost, one-time cost and future run-rate; <b>confidence</b>, <b>risk</b> and stakeholder-sensitivity; specific <b>negotiation levers</b> and a rationale. The same analysis is surfaced on that app\'s <b>Application Master Data</b> record (Licensing Analysis card). Every run is retained and never overwritten.',
    },
  ],
  links: [
    { href: '/apr/portfolio/cost', label: 'Current Run Rate' },
    { href: '/dashboard', label: 'Executive Dashboard' },
  ],
};

const LicensingAnalysis: React.FC = () => {
  const [sp, setSp] = useSearchParams();
  const [eligible, setEligible] = useState<any>(null);     // raw eligible apps grouped by family
  const [analysis, setAnalysis] = useState<any>(null);     // latest run's per-app results (merged in)
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [runMsg, setRunMsg] = useState<string | null>(null);
  const [q, setQ] = useState(sp.get('q') || '');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [selectedApp, setSelectedApp] = useState<any>(null);
  // EXPANDED parent groups, keyed by vendor_family. Tracking expansion rather than collapse
  // means the empty initial set already gives the default the user asked for — every family
  // collapsed on arrival — with no load-time effect to seed it, and newly appearing groups
  // (say after clearing the search) arrive collapsed too. Same shape as Licensing Results.
  const [expandedFamilies, setExpandedFamilies] = useState<Set<string>>(new Set());
  const [familyTab, setFamilyTab] = useState<any>(null);
  const [showRuns, setShowRuns] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  // How the run groups its applications for the family-level pass. 'vendor' matches the
  // methodology (one strategy per vendor — combined volume is the leverage); 'parent'
  // mirrors the hierarchy on Application Master Data. Chosen per run, stored on the run.
  const [grouping, setGrouping] = useState<'vendor' | 'parent'>('vendor');
  // Documents chosen from the central Licensing Knowledge Library. These ARE the analysis
  // basis for the run — per-application attachments are no longer consulted.
  const [docIds, setDocIds] = useState<string[]>([]);
  const [runs, setRuns] = useState<any[]>([]);
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'Administrator';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [el, an] = await Promise.all([
        apiService.getLicensingEligible(),
        apiService.getLicensingAnalysis({ run_id: sp.get('run_id') || undefined }).catch(() => null),
      ]);
      setEligible(el); setAnalysis(an);
    } finally { setLoading(false); }
  }, [sp]);
  useEffect(() => { load(); }, [load]);

  // map application_id -> analysis result (only for apps that have been run)
  const analysisByApp = useMemo(() => {
    const m = new Map<string, any>();
    (analysis?.apps || []).forEach((a: any) => m.set(a.application_id, a));
    return m;
  }, [analysis]);
  const familyMeta = useMemo(() => {
    const m = new Map<string, any>();
    (analysis?.families || []).forEach((f: any) => m.set(f.vendor_family, f));
    return m;
  }, [analysis]);
  const leversByApp = analysis?.levers || {};

  // merged, filtered families (raw eligible + inline analysis)
  const families = useMemo(() => {
    const src: any[] = eligible?.families || [];
    const ql = q.trim().toLowerCase();
    return src.map((f) => {
      const members = f.members
        .filter((m: any) => !ql || (m.application_name || '').toLowerCase().includes(ql) || (m.application_id_code || '').toLowerCase().includes(ql) || (m.vendor_name || '').toLowerCase().includes(ql) || (f.vendor_family || '').toLowerCase().includes(ql))
        .map((m: any) => ({ ...m, an: analysisByApp.get(m.application_id) }));
      return { ...f, members };
    }).filter((f) => f.members.length > 0);
  }, [eligible, q, analysisByApp]);

  const allIds = useMemo(() => families.flatMap((f) => f.members.map((m: any) => m.application_id)), [families]);

  const toggle = (id: string) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleFamily = (f: any, on: boolean) => setSelected((s) => { const n = new Set(s); f.members.forEach((m: any) => on ? n.add(m.application_id) : n.delete(m.application_id)); return n; });

  // Only multi-app / parented groups have children to hide — single-app "families" render as
  // a plain row with no group header, so they are never collapsible.
  const groupKeys = useMemo(
    () => families.filter((f) => f.members.length > 1 || !!f.parent_application_id_code).map((f) => f.vendor_family),
    [families],
  );
  const expandedCount = groupKeys.filter((k) => expandedFamilies.has(k)).length;
  const allExpanded = groupKeys.length > 0 && expandedCount === groupKeys.length;
  const toggleCollapse = (key: string) => setExpandedFamilies((prev) => {
    const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n;
  });
  const expandAll = () => setExpandedFamilies(new Set(groupKeys));
  const collapseAll = () => setExpandedFamilies(new Set());
  const setAll = (on: boolean) => setSelected(on ? new Set(allIds) : new Set());

  // The selected applications, resolved to their row details + vendor family (for the panel).
  const selectedApps = useMemo(() => {
    const map = new Map<string, any>();
    families.forEach((f) => f.members.forEach((m: any) => map.set(m.application_id, { ...m, vendor_family: f.vendor_family })));
    return Array.from(selected)
      .map((sid) => map.get(sid))
      .filter(Boolean)
      .map((m: any) => ({ ...m, analysed: analysisByApp.has(m.application_id) }));
  }, [families, selected, analysisByApp]);

  // Execute the run. Each vendor family resolves its own negotiation template server-side
  // (Microsoft apps → Microsoft module; everything else → Generic), so no instructions blob
  // is passed here — the panel auto-saves the open template's edits/config before calling this.
  const executeRun = async (runAppIds?: string[]) => {
    // The dialog confirms the final list — every application ticked by default, minus any
    // the operator dropped there. Falls back to the table selection if nothing was passed.
    const ids = (runAppIds && runAppIds.length) ? runAppIds : Array.from(selected);
    if (!ids.length) return;
    // Warn if any selected app already has a licensing analysis — re-running adds another.
    const already = ids.filter((id) => analysisByApp.has(id));
    if (already.length > 0) {
      const ok = window.confirm(
        `A licensing analysis already exists for ${already.length} of the selected application(s).\n\n` +
        `Running now creates an ADDITIONAL analysis that becomes the CURRENT one shown here. ` +
        `The previous run is NOT deleted — it is kept in Licensing Results as part of the application's history.\n\n` +
        `Are you sure you want an additional analysis?`
      );
      if (!ok) return;
    }
    setShowInstructions(false);
    setRunning(true);
    setRunMsg(`Starting analysis for ${ids.length} application(s) — grouped by ${grouping === 'parent' ? 'parent tree' : 'vendor family'}…`);
    try {
      const { run_id } = await apiService.runLicensingAnalysis(ids, undefined, undefined, undefined, grouping, docIds);
      for (let i = 0; i < 400; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const stt = await apiService.getLicensingRunStatus(run_id);
        setRunMsg(`Analysing… ${stt.analysed || 0} of ${ids.length} (${stt.status})`);
        if (stt.status === 'completed' || stt.status === 'failed') { setRunMsg(stt.status === 'completed' ? `Done — analysed ${stt.analysed} application(s).` : `Failed: ${stt.error || 'error'}`); break; }
      }
      await load();
    } catch (e: any) { setRunMsg('Error: ' + (e?.response?.data?.error || e.message)); }
    finally { setRunning(false); }
  };

  const openRuns = async () => {
    setShowRuns(true);
    try {
      const r = await apiService.getLicensingRuns();
      setRuns(r?.runs || []);
    } catch { /* */ }
  };
  const deleteRun = async (runId: string) => {
    if (!window.confirm('Delete this licensing run and all of its results (analysis, levers)? This cannot be undone.')) return;
    try {
      await apiService.deleteLicensingRun(runId);
      setSp({});
      await load();
      await openRuns();
    } catch (e: any) {
      alert(e?.response?.data?.error || e?.message || 'Delete failed');
    }
  };

  const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

  // one row per application (App ID + name merged on a single line).
  // `indent` = true for children (they sit under a parent group row).
  const renderAppRow = (m: any, indent = false) => {
    const a = m.an;
    const created = analysis?.run?.finished_at ? new Date(analysis.run.finished_at).toLocaleDateString() : '';
    return (
      <tr key={m.application_id} style={{ borderTop: '1px solid #f3f4f6', cursor: a ? 'pointer' : 'default' }} onClick={() => a && setSelectedApp(a)}>
        {/* A child's checkbox is inset with the row so it reads as belonging to that child.
            The └ connector leads the checkbox rather than sitting between it and the App ID —
            otherwise the connector plus the status badge pushed the two ~53px apart. */}
        <td style={{ ...td, paddingLeft: indent ? CHILD_INDENT : 12, paddingRight: 0, whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
          {indent && <span style={{ color: '#cbd5e1', marginRight: 6 }}>└</span>}
          <input type="checkbox" checked={selected.has(m.application_id)} onChange={() => toggle(m.application_id)} />
        </td>
        <td style={{ ...td, paddingLeft: 8 }}>
          <span
            title={a
              ? `Licensing analysis on record${created ? ` · created ${created}` : ''} — see all runs in Licensing Results`
              : 'No licensing analysis yet — tick and Run analysis'}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 16, height: 16, borderRadius: 8, marginRight: 8, fontSize: 11, fontWeight: 700,
              color: a ? '#fff' : '#94a3b8', background: a ? '#16a34a' : '#e5e7eb', cursor: 'default',
            }}>{a ? '✓' : '–'}</span>
          <span style={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, color: '#111827', marginRight: 8 }}>{m.application_id_code || '—'}</span>
          <span style={{ fontWeight: 600, color: '#111827' }}>{m.application_name}</span>
          {m.has_document
            ? <span style={{ marginLeft: 8, fontSize: 11, color: '#16a34a' }}>✓ doc</span>
            : <span title="No licensing information from customer" style={{ marginLeft: 8, fontSize: 11, color: '#d97706' }}>⚠ no license data</span>}
        </td>
        <td style={td}>{m.vendor_name || '—'}</td>
        <td style={tdR}>{money(m.annual_license_cost)}</td>
        {a ? <>
          <td style={td}><Badge text={a.disposition} color={DISPO_COLOR[a.disposition] || '#6b7280'} />{a.decision_rule_flag && <span title="Fails the decision rule" style={{ marginLeft: 4, color: '#dc2626' }}>!</span>}</td>
          {/* Low · Mid · High on ONE row at a single font size — Mid stays green and bold to
              mark it as the proposed basis; Low and High keep their muted grey. */}
          <td style={{ ...tdR, whiteSpace: 'nowrap' }}>
            <div style={{ display: 'inline-flex', alignItems: 'baseline', gap: 10, fontSize: 12 }}>
              <span style={{ color: '#9ca3af' }}>L {money(a.savings_low)} ({pctOf(a.savings_low, a.annual_license_cost)})</span>
              <span style={{ color: '#16a34a', fontWeight: 700 }}>M {money(a.savings_mid)} ({pctOf(a.savings_mid, a.annual_license_cost)})</span>
              <span style={{ color: '#9ca3af' }}>H {money(a.savings_high)} ({pctOf(a.savings_high, a.annual_license_cost)})</span>
            </div>
          </td>
          <td style={tdR}><strong>{money(a.new_license_mid)}</strong></td>
          <td style={td}><span style={{ fontSize: 11 }}>{a.confidence}</span> · <RiskBadge level={a.risk} /></td>
          <td style={{ ...td, textAlign: 'center' }}>
            <span title="Open analysis" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: 6, color: '#4f46e5', fontSize: 18, fontWeight: 700, lineHeight: 1 }}>›</span>
          </td>
        </> : (
          <td style={{ ...td, color: '#9ca3af', fontStyle: 'italic' }} colSpan={5}>Not analysed yet — tick and Run analysis</td>
        )}
      </tr>
    );
  };

  return (
    <div className="page licensing-page page-fixed-header" style={{ padding: '1.5rem' }}>
      {/* Same shared PageHeader as the Licensing Results screen, so both License Intelligence
          screens share one heading layout. The primary action goes in its actions slot. */}
      {/* Title only — the actions live in the toolbar row below, exactly as on Licensing
          Results. The header's right side stays clear, which is where the shared fixed
          "How this works" launcher sits. */}
      <PageHeader
        title="Licensing Analysis"
        subtitle="Size the renegotiation opportunity across your licence spend — and act while renewal dates still give you leverage."
        actions={
          <>
            <span style={{ fontSize: 12.5, color: selected.size ? '#37474f' : '#8a95a8', fontWeight: selected.size ? 700 : 400 }}>
              {selected.size} selected
            </span>
            <button onClick={() => setShowInstructions(true)} disabled={running || selected.size === 0}
              style={{ ...btnPrimary, opacity: (running || selected.size === 0) ? 0.5 : 1, cursor: (running || selected.size === 0) ? 'default' : 'pointer' }}
              title={selected.size === 0 ? 'Tick one or more applications first' : `Run the licensing analysis on ${selected.size} application${selected.size === 1 ? '' : 's'}`}>
              {running ? 'Running…' : `▶ Run analysis${selected.size ? ` (${selected.size})` : ''}`}
            </button>
          </>
        }
      />

      {/* Only this scrolls — the header above stays put. */}
      <div className="scrollable-data-section">

      {runMsg && <div style={{ ...card, marginBottom: 12, background: '#eff6ff', borderColor: '#bfdbfe', fontSize: 13, color: '#1e40af' }}>{runMsg}</div>}

      {/* Selection guidance and the no-licensing-document caveat both live in "How this
          works" (see LicensingAnalysisHelp), so the standing banner that repeated them was
          removed. Per-row ⚠ markers still flag apps with no licensing information. */}

      {/* The portfolio subtotal cards that sat here now live on Licensing Results, which is
          where the question "what does this add up to" belongs. This screen is for choosing
          what to analyse and running it; the per-row figures below carry the detail. */}

      {/* filter + how it works */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={q} onChange={(e) => { setQ(e.target.value); setSp(e.target.value ? { q: e.target.value } : {}); }}
          placeholder="Search App ID, description, or vendor…"
          style={{ flex: '1 1 260px', minWidth: 220, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }} />
        {/* Same controls and styling as the Licensing Results screen. */}
        {groupKeys.length > 0 && (
          <div style={{ display: 'inline-flex', gap: 6 }}>
            <button onClick={expandAll} disabled={allExpanded}
              style={{ ...btnTree, opacity: allExpanded ? 0.45 : 1, cursor: allExpanded ? 'default' : 'pointer' }}
              title={`Expand all ${groupKeys.length} parent group${groupKeys.length === 1 ? '' : 's'}`}>⊞ Expand all</button>
            <button onClick={collapseAll} disabled={expandedCount === 0}
              style={{ ...btnTree, opacity: expandedCount === 0 ? 0.45 : 1, cursor: expandedCount === 0 ? 'default' : 'pointer' }}
              title="Collapse all parent groups">⊟ Collapse all</button>
          </div>
        )}
        {/* Run analysis and the selected count live in the page heading; the toolbar keeps
            only the filter, the tree controls and the total. */}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 12.5, color: '#8a95a8' }}>
            {loading ? 'Loading…' : `${allIds.length} application${allIds.length === 1 ? '' : 's'}`}
          </span>
        </div>
        <HelpPanel help={LicensingAnalysisHelp} />
      </div>

      {/* raw eligible list, grouped by partner, with inline analysis */}
      {loading ? <div style={{ color: '#6b7280', padding: 20 }}>Loading…</div>
        : families.length === 0 ? <div style={{ ...card, color: '#6b7280' }}>No eligible applications match the filter.</div>
        : (
          <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f9fafb', textAlign: 'left', color: '#6b7280', fontSize: 11, textTransform: 'uppercase' }}>
                    <th style={{ ...th, width: 32 }} title="Select all">
                      <input type="checkbox" checked={allIds.length > 0 && allIds.every((id) => selected.has(id))} ref={(el) => { if (el) el.indeterminate = selected.size > 0 && !allIds.every((id) => selected.has(id)); }} onChange={(e) => setAll(e.target.checked)} />
                    </th>
                    <th style={th}>App ID · Application</th><th style={th}>Vendor</th><th style={thR}>Current cost</th>
                    <th style={th}>Disposition</th><th style={thR}>Savings — Low · <span style={{ color: '#16a34a' }}>Mid</span> · High</th>
                    <th style={thR}>Proposed<div style={{ fontWeight: 400, textTransform: 'none', color: '#9ca3af' }}>(current − Mid)</div></th>
                    <th style={th}>Conf · Risk</th><th style={th}></th>
                  </tr>
                </thead>
                <tbody>
                  {families.map((f) => {
                    const all = f.members.every((m: any) => selected.has(m.application_id));
                    const some = f.members.some((m: any) => selected.has(m.application_id));
                    const meta = familyMeta.get(f.vendor_family);
                    // single-app "families" (no parent, one member) render as a plain app row — no group header
                    const isGroup = f.members.length > 1 || !!f.parent_application_id_code;
                    const isCollapsed = !expandedFamilies.has(f.vendor_family);
                    if (!isGroup) {
                      return <React.Fragment key={f.vendor_family}>{f.members.map((m: any) => renderAppRow(m, false))}</React.Fragment>;
                    }
                    return (
                      <React.Fragment key={f.vendor_family}>
                        <tr style={{ background: '#eef2ff' }}>
                          <td style={{ ...td, paddingRight: 0 }}><input type="checkbox" checked={all} ref={(el) => { if (el) el.indeterminate = some && !all; }} onChange={(e) => toggleFamily(f, e.target.checked)} /></td>
                          <td style={{ ...td, paddingLeft: 10, fontWeight: 700 }} title={familyCompositionTitle(f) || (f.parent_application_name ? `Parent: ${f.parent_application_name}` : undefined)}>
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleCollapse(f.vendor_family); }}
                              title={isCollapsed ? `Show the ${f.members.length} applications in this group` : 'Hide this group’s applications'}
                              aria-expanded={!isCollapsed}
                              style={{
                                background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginRight: 8,
                                fontSize: 12, color: '#4338ca', width: 14, textAlign: 'left',
                                transform: isCollapsed ? 'rotate(0deg)' : 'rotate(90deg)', transition: 'transform 0.15s',
                              }}>▶</button>
                            {/* A vendor family can span several parent trees plus standalones.
                                Showing a single parent code then reads as though every member is
                                that parent's child, so the code is only shown when it is actually
                                true of the whole group. Otherwise the split is spelled out. */}
                            {isSingleParentFamily(f)
                              ? <span style={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, color: '#4338ca', marginRight: 8 }}>{f.parent_application_id_code}</span>
                              : null}
                            {f.vendor_family}
                            <span style={{ color: '#6b7280', fontWeight: 400 }}> · {f.members.length} apps{familySplitLabel(f)}</span>
                            {!f.has_document && <span style={{ marginLeft: 8, fontSize: 11, color: '#d97706', fontWeight: 400 }}>⚠ no licensing document</span>}
                            {meta && <button onClick={() => setFamilyTab({ ...f, meta })} style={{ marginLeft: 8, fontSize: 11, color: '#4f46e5', background: 'none', border: '1px solid #c7d2fe', borderRadius: 6, padding: '1px 7px', cursor: 'pointer' }}>Group playbook →</button>}
                          </td>
                          <td style={td}></td>
                          <td style={tdR}><strong>{money(f.family_current_cost)}</strong></td>
                          <td style={td}></td>
                          {/* Same one-row Low · Mid · High treatment as the application rows. */}
                          <td style={{ ...tdR, whiteSpace: 'nowrap' }}>{meta ? (
                            <div style={{ display: 'inline-flex', alignItems: 'baseline', gap: 10, fontSize: 12 }}>
                              <span style={{ color: '#9ca3af' }}>L {money(meta.family_savings_low)}</span>
                              <span style={{ color: '#16a34a', fontWeight: 700 }}>M {money(meta.family_savings_mid)}</span>
                              <span style={{ color: '#9ca3af' }}>H {money(meta.family_savings_high)}</span>
                            </div>
                          ) : <span style={{ color: '#9ca3af' }}>—</span>}</td>
                          <td style={tdR}>{meta ? <strong>{money((meta.family_current_cost || 0) - (meta.family_savings_mid || 0))}</strong> : ''}</td>
                          <td style={td}></td><td style={td}></td>
                        </tr>
                        {!isCollapsed && f.members.map((m: any) => renderAppRow(m, true))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

      <div style={{ ...card, marginTop: 14, background: '#f9fafb', fontSize: 12, color: '#4b5563' }}>
        <strong>Methodology caveats.</strong> Savings are industry-benchmark estimates anchored to current cost — not contract-validated. They are steady-state (year-2+); one-time migration/PS/parallel-running costs are shown separately and NOT netted (year-1 realisation is partial). Replacement plays assume 6–18 month feasibility and exclude non-licensing TCO. The <strong>Mid</strong> value is the proposed savings basis (proposed cost = current − Mid). Confirm against actual contract terms before a business case.
      </div>

      </div>

      {showInstructions && <InstructionsPanel selectedApps={selectedApps} running={running} isAdmin={isAdmin} grouping={grouping} onGroupingChange={setGrouping} docIds={docIds} onDocIdsChange={setDocIds} onExecute={executeRun} onClose={() => setShowInstructions(false)} />}
      {selectedApp && <AppDrawer app={selectedApp} levers={leversByApp[selectedApp.application_id] || []} onClose={() => setSelectedApp(null)} />}
      {familyTab && <FamilyDrawer family={familyTab} onClose={() => setFamilyTab(null)} />}
      {showRuns && <RunsDrawer runs={runs} isAdmin={isAdmin} onDelete={deleteRun} onClose={() => setShowRuns(false)} />}
    </div>
  );
};

// ── sub-components ────────────────────────────────────────────────────────────
const Kpi: React.FC<{ label: string; value: string; sub?: string; color?: string }> = ({ label, value, sub, color }) => (
  <div style={{ ...card, padding: 14 }}>
    <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', fontWeight: 600 }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 700, color: color || '#111827', marginTop: 4 }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: '#6b7280' }}>{sub}</div>}
  </div>
);
const Drawer: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({ title, onClose, children }) => (
  <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1300, display: 'flex', justifyContent: 'flex-end' }}>
    <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(680px, 96vw)', height: '100%', background: '#fff', overflowY: 'auto', boxShadow: '-8px 0 24px rgba(0,0,0,0.2)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, background: '#fff' }}>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{title}</h2>
        <button onClick={onClose} style={{ border: 'none', background: '#fee2e2', color: '#b91c1c', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', fontSize: 16 }}>✕</button>
      </div>
      <div style={{ padding: 18 }}>{children}</div>
    </div>
  </div>
);
const AppDrawer: React.FC<{ app: any; levers: any[]; onClose: () => void }> = ({ app, levers, onClose }) => {
  const flags: string[] = Array.isArray(app.data_quality_flags) ? app.data_quality_flags : (() => { try { return JSON.parse(app.data_quality_flags || '[]'); } catch { return []; } })();
  return (
    <Drawer title={app.application_name} onClose={onClose}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <Badge text={app.disposition} color={DISPO_COLOR[app.disposition] || '#6b7280'} />
        <span style={{ fontSize: 12, color: '#6b7280' }}>{app.application_id_code} · {app.vendor} · {app.vendor_family}</span>
        {app.application_id && (
          <a href={`/ai-analysis/licensing/results?applicationId=${app.application_id}`}
            style={{ marginLeft: 'auto', fontSize: 12.5, color: '#1565c0', fontWeight: 600, textDecoration: 'none' }}>
            See all runs →
          </a>
        )}
      </div>
      <div style={{ fontSize: 11.5, color: '#8a95a8', marginBottom: 12 }}>
        This is the <strong>current</strong> analysis. Every prior run is kept in <strong>Licensing Results</strong>.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 14 }}>
        <Kpi label="Current" value={money(app.annual_license_cost)} />
        <Kpi label="Proposed (from Mid)" value={money(app.new_license_mid)} />
        <Kpi label="Savings — Low·Mid·High" value={money(app.savings_mid)} sub={`${money(app.savings_low)} · ${money(app.savings_mid)} · ${money(app.savings_high)}`} color="#16a34a" />
        <Kpi label="One-time cost" value={money(app.one_time_cost)} />
        <Kpi label="Future run-rate" value={money(app.future_run_rate)} />
        <Kpi label="Confidence · Risk" value={`${app.confidence} · ${app.risk}`} />
      </div>
      {app.successor_or_platform && <p style={{ fontSize: 13 }}><strong>Successor / platform:</strong> {app.successor_or_platform}</p>}
      {app.rationale && <><h4 style={h4}>Rationale</h4><p style={p}>{app.rationale}</p></>}
      {app.methodology_note && <><h4 style={h4}>Methodology note</h4><p style={p}>{app.methodology_note}</p></>}
      {levers.length > 0 && <><h4 style={h4}>Negotiation levers</h4><ul style={{ margin: 0, paddingLeft: 18 }}>{levers.map((l, i) => <li key={i} style={{ fontSize: 13, marginBottom: 4 }}><strong>{l.lever_type}</strong> — {l.lever_text} <span style={{ color: '#9ca3af' }}>(P{l.priority})</span></li>)}</ul></>}
      {app.year1_realization_note && <p style={{ ...p, marginTop: 12, fontStyle: 'italic', color: '#6b7280' }}>{app.year1_realization_note}</p>}
      {flags.length > 0 && <div style={{ ...card, marginTop: 12, background: '#fffbeb', borderColor: '#fde68a' }}><strong style={{ fontSize: 12, color: '#92400e' }}>Data-quality flags</strong><ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>{flags.map((f, i) => <li key={i} style={{ fontSize: 12, color: '#92400e' }}>{f}</li>)}</ul></div>}
    </Drawer>
  );
};
const FamilyDrawer: React.FC<{ family: any; onClose: () => void }> = ({ family, onClose }) => {
  const m = family.meta || {};
  return (
    <Drawer title={`${family.vendor_family} — Group negotiation`} onClose={onClose}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10, marginBottom: 14 }}>
        <Kpi label="Combined spend" value={money(m.family_current_cost ?? family.family_current_cost)} />
        <Kpi label="Blended savings (Mid)" value={money(m.family_savings_mid)} color="#16a34a" />
      </div>
      {m.master_ask && <><h4 style={h4}>Master ask</h4><p style={p}>{m.master_ask}</p></>}
      {m.negotiation_strategy && <><h4 style={h4}>Strategy</h4><p style={p}>{m.negotiation_strategy}</p></>}
      {m.sequencing && <><h4 style={h4}>Sequencing</h4><p style={p}>{m.sequencing}</p></>}
      <h4 style={h4}>Applications</h4>
      <ul style={{ margin: 0, paddingLeft: 18 }}>{family.members.map((r: any) => <li key={r.application_id} style={{ fontSize: 13, marginBottom: 3 }}>{r.application_name}{r.an && <> — <Badge text={r.an.disposition} color={DISPO_COLOR[r.an.disposition] || '#6b7280'} /> · save {money(r.an.savings_mid)}</>}</li>)}</ul>
    </Drawer>
  );
};
const RunsDrawer: React.FC<{ runs?: any[]; isAdmin?: boolean; onDelete?: (runId: string) => void; onClose: () => void }> = ({ runs = [], isAdmin, onDelete, onClose }) => (
  <Drawer title="Runs" onClose={onClose}>
    <h4 style={{ ...h4, marginTop: 0 }}>All runs {isAdmin && <span style={{ fontSize: 11, fontWeight: 400, color: '#6b7280' }}>· Administrators can delete any run</span>}</h4>
    {runs.length === 0 ? <p style={p}>No runs yet.</p> :
      runs.map((r) => {
        const t = (() => { try { return typeof r.totals === 'string' ? JSON.parse(r.totals) : r.totals; } catch { return {}; } })();
        return (
          <div key={r.run_id} style={{ ...card, marginBottom: 8, padding: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <div>
                <strong style={{ fontSize: 13 }}>{r.status === 'completed' ? '✓' : r.status} · {r.app_count || 0} apps</strong>
                <div style={{ fontSize: 12, color: '#6b7280' }}>run {String(r.run_id).slice(0, 8)} · {r.finished_at ? new Date(r.finished_at).toLocaleString() : (r.started_at ? new Date(r.started_at).toLocaleString() : '')}{t?.savings_mid != null ? ` · save ${money(t.savings_mid)}` : ''}</div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {isAdmin && onDelete && (
                  <button onClick={() => onDelete(r.run_id)} style={{ ...btnGhost, borderColor: '#fca5a5', color: '#b91c1c', background: '#fef2f2' }}>🗑 Delete</button>
                )}
              </div>
            </div>
          </div>
        );
      })}
  </Drawer>
);

// Compile the one-line run-config echo from a template's config schema + values (mirrors the server).
const runConfigLine = (schema: any[], cfg: Record<string, string>): string =>
  'run: ' + (schema || []).filter((f: any) => f.rc).map((f: any) => `${f.rc.abbr}=${cfg[f.key]}${f.rc.suffix || ''}`).join(' · ');
const effectiveConfig = (schema: any[], config: any): Record<string, string> => {
  const o: Record<string, string> = {};
  (schema || []).forEach((f: any) => {
    const v = config && config[f.key];
    o[f.key] = (v != null && Array.isArray(f.values) && f.values.includes(String(v))) ? String(v) : f.default;
  });
  return o;
};
// Which template applies to a vendor family: loose vendor match, else Generic (mirrors the server).
const resolveTemplateLocal = (prompts: any[], vendorFamily: string): any => {
  const norm = String(vendorFamily || '').trim().toLowerCase();
  if (norm) {
    const m = prompts.find((t) => { const tf = String(t.vendor_family || '').trim().toLowerCase(); return tf && (tf === norm || norm.includes(tf) || tf.includes(norm)); });
    if (m) return m;
  }
  return prompts.find((t) => t.prompt_key === 'generic') || prompts.find((t) => !t.vendor_family) || null;
};

// ── Instructions panel — prompt library + editable, configurable negotiation templates ─
// Opens on the right when the user hits "Run analysis". Each vendor family runs with its own
// template (Microsoft apps → Microsoft module, everything else → Generic). Vendor modules with
// a §1 configuration expose interactive knobs that compile into the instructions. Execute
// auto-saves the open template's edits/config, then runs.
const InstructionsPanel: React.FC<{ selectedApps: any[]; running: boolean; isAdmin: boolean; grouping: 'vendor' | 'parent'; onGroupingChange: (g: 'vendor' | 'parent') => void; docIds: string[]; onDocIdsChange: (ids: string[]) => void; onExecute: (runAppIds: string[]) => void; onClose: () => void }> = ({ selectedApps, running, isAdmin, grouping, onGroupingChange, docIds, onDocIdsChange, onExecute, onClose }) => {
  const [libDocs, setLibDocs] = useState<any[]>([]);
  const [libLoading, setLibLoading] = useState(true);
  /** Filter for the contract-document column — the point of the column at 20-30 files. */
  const [docFilter, setDocFilter] = useState('');
  // Applications to actually run, ticked by default. This is the operator's last chance to
  // drop one before spending tokens on it, so it starts as the full table selection and is
  // resynced whenever that selection changes.
  const [includedApps, setIncludedApps] = useState<Set<string>>(() => new Set(selectedApps.map((a) => a.application_id)));
  useEffect(() => {
    setIncludedApps(new Set(selectedApps.map((a: any) => a.application_id)));
  }, [selectedApps]);
  const toggleApp = (id: string) => setIncludedApps((prev) => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const allIncluded = selectedApps.length > 0 && includedApps.size === selectedApps.length;
  useEffect(() => {
    let alive = true;
    apiService.getLicensingDocuments()
      .then((r) => { if (alive) setLibDocs(Array.isArray(r?.documents) ? r.documents : []); })
      .catch(() => { if (alive) setLibDocs([]); })
      .finally(() => { if (alive) setLibLoading(false); });
    return () => { alive = false; };
  }, []);
  const [prompts, setPrompts] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [body, setBody] = useState('');
  const [schema, setSchema] = useState<any[]>([]);
  const [config, setConfig] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  const [deleteMode, setDeleteMode] = useState(false);       // multi-select delete (Administrators only)
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [vendorFamily, setVendorFamily] = useState<string | null>(null);
  const [linkedAppIds, setLinkedAppIds] = useState<string[]>([]);   // applications this prompt is dedicated to (routing)
  const [isDraft, setIsDraft] = useState(false);              // an AI-generated, not-yet-saved module
  const [showGenerate, setShowGenerate] = useState(false);    // New-prompt generator modal (admin)
  const [apps, setApps] = useState<any[] | null>(null);       // all applications, loaded lazily (for app-name labels)
  const [replacePromptIds, setReplacePromptIds] = useState<string[]>([]);  // prompts to delete on save (replace flow)

  const pick = (p: any) => {
    setSelectedId(p.id); setName(p.name || ''); setDescription(p.description || ''); setBody(p.body || '');
    const sc = Array.isArray(p.config_schema) ? p.config_schema : [];
    setSchema(sc); setConfig(effectiveConfig(sc, p.config));
    setVendorFamily(p.vendor_family || null);
    setLinkedAppIds(Array.isArray(p.application_ids) ? p.application_ids : []);
    setIsDraft(false);
  };

  const loadPrompts = useCallback(async (preferId?: string) => {
    const rows = await apiService.getLicensingInstructions();
    setPrompts(rows || []);
    if (rows && rows.length) {
      let chosen = preferId ? rows.find((r: any) => r.id === preferId) : null;
      if (!chosen) {
        const vendors = selectedApps.map((a) => (a.vendor_family || a.vendor_name || '').toLowerCase());
        chosen = rows.find((r: any) => r.vendor_family && vendors.some((v: string) => v.includes(String(r.vendor_family).toLowerCase())));
      }
      pick(chosen || rows[0]);
    }
  }, [selectedApps]);

  useEffect(() => { loadPrompts().catch(() => {}); ensureApps();   }, []);

  const flashMsg = (m: string) => { setFlash(m); setTimeout(() => setFlash(null), 1800); };

  // Re-arrange the library: move a prompt up/down and persist the new order.
  const movePrompt = async (index: number, dir: number) => {
    const j = index + dir;
    if (j < 0 || j >= prompts.length) return;
    const next = [...prompts];
    const [item] = next.splice(index, 1);
    next.splice(j, 0, item);
    setPrompts(next); // optimistic
    try { await apiService.reorderLicensingInstructions(next.map((p) => p.id)); }
    catch (e: any) { alert(e?.response?.data?.error || e.message); await loadPrompts(selectedId || undefined); }
  };
  const payload = () => ({ name, description, body, config: schema.length ? config : undefined });

  // Load all applications once (for the generator + link picker).
  const ensureApps = useCallback(async () => {
    if (apps) return;
    try { const list = await apiService.getApplications(); setApps(Array.isArray(list) ? list : []); }
    catch { setApps([]); }
  }, [apps]);

  // "New prompt" (Administrator only): open the generator, PRE-SELECTING the run-selected apps
  // that don't already have a dedicated prompt (so generating for e.g. SAP Concur starts linked).
  const openGenerate = () => {
    ensureApps();
    const ownerOf = (aid: string) => prompts.find((pp) => Array.isArray(pp.application_ids) && pp.application_ids.includes(aid)) || null;
    const conflictPromptIds = new Set<string>();
    const conflictLines: string[] = [];
    selectedApps.forEach((a) => { const p = ownerOf(a.application_id); if (p) { conflictPromptIds.add(p.id); conflictLines.push(`• ${a.application_id_code} (${a.application_name}) → "${p.name}"`); } });
    // If any selected app already has a prompt, WARN and let the admin choose: stop, or replace it.
    if (conflictPromptIds.size) {
      const ok = window.confirm(
        `A dedicated prompt already exists for:\n${conflictLines.join('\n')}\n\n` +
        `OK  = generate a NEW prompt and REPLACE the existing one(s).\n` +
        `Cancel = stop and keep the existing prompt(s) (edit them in the library instead).`
      );
      if (!ok) return;
      setReplacePromptIds(Array.from(conflictPromptIds));   // deleted after the new one is generated
    } else {
      setReplacePromptIds([]);
    }
    setSelectedId(null); setIsDraft(false);
    setLinkedAppIds(selectedApps.map((a) => a.application_id));  // link all selected apps (owners get replaced)
    setShowGenerate(true);
  };
  const onGenerate = async (inputs: { vendor: string; lifecycle_stage: string; commercial_model?: string; notes?: string }) => {
    // Conflicts remaining after accounting for the prompts we're about to replace (should be none).
    const conflicts = linkedAppIds
      .map((aid) => { const owner = prompts.find((p) => Array.isArray(p.application_ids) && p.application_ids.includes(aid) && !replacePromptIds.includes(p.id)); return owner ? { aid, prompt: owner.name } : null; })
      .filter(Boolean) as { aid: string; prompt: string }[];
    if (conflicts.length) {
      const label = (aid: string) => { const a = (apps || []).find((x) => x.id === aid); return a ? `${a.applicationIdCode || ''} (${a.name})` : aid; };
      alert(`A dedicated prompt already exists for:\n${conflicts.map((c) => `• ${label(c.aid)} → "${c.prompt}"`).join('\n')}\n\nModify that existing prompt, or delete it and then create a new one.`);
      return;
    }
    setBusy(true);
    try {
      // async job + poll (keeps each request short — App-Runner/platform request-timeout safe)
      const { job_id } = await apiService.generateLicensingModule({ ...inputs, application_ids: linkedAppIds, replace: replacePromptIds.length > 0 });
      let draft: any = null;
      for (let i = 0; i < 200; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const j = await apiService.getLicensingModuleJob(job_id);
        if (j.status === 'done') { draft = j.draft; break; }
        if (j.status === 'error') { throw new Error(j.error || 'Generation failed'); }
      }
      if (!draft) throw new Error('Generation timed out — please try again.');
      // REPLACE: now that the new content is in hand, delete the prompt(s) being replaced so their
      // applications are freed (done AFTER generation so a failed/cancelled run keeps the old prompt).
      if (replacePromptIds.length) {
        await Promise.all(replacePromptIds.map((pid) => apiService.deleteLicensingInstruction(pid).catch(() => {})));
      }
      // Auto-save the generated module as a new prompt, then open it for any further edits.
      const sc = Array.isArray(draft?.config_schema) ? draft.config_schema : [];
      const created = await apiService.createLicensingInstruction({
        name: draft?.name || 'New module', description: draft?.description || '', body: draft?.body || '',
        vendor_family: draft?.vendor_family || undefined,
        config_schema: sc.length ? sc : undefined,
        config: sc.length ? (draft?.config || undefined) : undefined,
        application_ids: linkedAppIds,
      });
      setReplacePromptIds([]);
      setShowGenerate(false);
      await loadPrompts(created?.id);   // refreshes the library and selects the new saved prompt
      flashMsg(replacePromptIds.length ? 'Prompt replaced & saved' : 'Prompt generated & saved');
    } catch (e: any) { alert(e?.response?.data?.error || e.message); }
    finally { setBusy(false); }
  };
  // Delete is Administrator-only and multi-select: entering delete mode reveals a checkbox on
  // every prompt; the admin ticks one or many, then confirms. Deleting removes each prompt and
  // its content (built-ins are durably hidden server-side so they don't return on restart).
  const toggleChecked = (id: string) => setChecked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const exitDeleteMode = () => { setDeleteMode(false); setChecked(new Set()); };
  const onBulkDelete = async () => {
    const ids = Array.from(checked);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} prompt(s) and their content? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await Promise.all(ids.map((pid) => apiService.deleteLicensingInstruction(pid)));
      if (selectedId && ids.includes(selectedId)) setSelectedId(null);
      exitDeleteMode();
      await loadPrompts();
      flashMsg(`Deleted ${ids.length}`);
    } catch (e: any) { alert(e?.response?.data?.error || e.message); }
    finally { setBusy(false); }
  };
  const onCopy = async () => { const text = schema.length ? `${runConfigLine(schema, config)}\n\n${body}` : body; try { await navigator.clipboard.writeText(text); flashMsg('Copied'); } catch { flashMsg('Copy failed'); } };
  // Save: a generated draft (no id) is CREATED; an existing prompt is UPDATED. Both persist the app links.
  const onSave = async () => {
    setBusy(true);
    try {
      if (isDraft || !selectedId) {
        const created = await apiService.createLicensingInstruction({
          name: name || 'New module', description, body, vendor_family: vendorFamily || undefined,
          config_schema: schema.length ? schema : undefined, config: schema.length ? config : undefined,
          application_ids: linkedAppIds,
        });
        setIsDraft(false);
        await loadPrompts(created?.id);
        flashMsg('Saved');
      } else {
        await apiService.updateLicensingInstruction(selectedId, { name, description, body, config: schema.length ? config : undefined, application_ids: linkedAppIds });
        await loadPrompts(selectedId);
        flashMsg('Saved');
      }
    } catch (e: any) { alert(e?.response?.data?.error || e.message); }
    finally { setBusy(false); }
  };
  const onReset = async () => {
    if (!selectedId) return;
    if (!window.confirm('Reset this template to its default text? Your edits to it will be discarded.')) return;
    setBusy(true);
    try { await apiService.resetLicensingInstruction(selectedId); await loadPrompts(selectedId); flashMsg('Reset to default'); }
    catch (e: any) { alert(e?.response?.data?.error || e.message); }
    finally { setBusy(false); }
  };

  // Persist the open template's edits/config, then run (passing the docs the operator opted out of).
  const onExecuteClick = async () => {
    if (selectedId) { try { await apiService.updateLicensingInstruction(selectedId, payload()); } catch { /* run anyway */ } }
    onExecute(selectedApps.map((a: any) => a.application_id).filter((id: string) => includedApps.has(id)));
  };

  const reruns = selectedApps.filter((a) => a.analysed).length;

  // Per-template application preview: how the selection splits across templates at run time.
  const appliedPreview = useMemo(() => {
    const counts = new Map<string, { name: string; n: number }>();
    selectedApps.forEach((a) => {
      const t = resolveTemplateLocal(prompts, a.vendor_family);
      const key = t?.id || 'none';
      const nm = t?.name || 'Generic';
      const e = counts.get(key) || { name: nm, n: 0 }; e.n += 1; counts.set(key, e);
    });
    return Array.from(counts.values()).sort((x, y) => y.n - x.n);
  }, [prompts, selectedApps]);

  return (
    <>
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1300, display: 'flex', justifyContent: 'flex-end' }}>
      {/* 1200px — the 1000 it was, plus the 20% the owner asked for, which is what pays
          for the third column. */}
      <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(1200px, 98vw)', height: '100%', background: '#fff', display: 'flex', flexDirection: 'column', boxShadow: '-8px 0 24px rgba(0,0,0,0.2)' }}>
        {/* header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid #e5e7eb' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Negotiation instructions</h2>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{selectedApps.length} application(s) selected · each vendor runs with its own template + its contract document</div>
          </div>
          <button onClick={onClose} title="Close" aria-label="Close"
            style={{ width: 36, height: 36, borderRadius: 9, border: '1.5px solid #dc2626', background: '#fee2e2', color: '#b91c1c', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <i className="material-icons" style={{ fontSize: 20 }}>close</i>
          </button>
        </div>

        {/* licensing contract documents (the foundation for the analysis) + applied-template preview (pinned) */}
        <div style={{ padding: '10px 18px', borderBottom: '1px solid #eef2f7', background: '#f8fafc', maxHeight: 148, overflowY: 'auto' }}>
          {/* These were per-application "use this app's contract document" toggles, disabled
              whenever the app had no document of its own — which, since documents moved to the
              central library, is every app, so they rendered permanently off and unclickable.
              They now do the thing they always looked like they did: choose which applications
              actually run. Ticked by default; untick to drop one before spending tokens. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <input type="checkbox" checked={allIncluded} disabled={running || selectedApps.length === 0}
              ref={(el) => { if (el) el.indeterminate = includedApps.size > 0 && !allIncluded; }}
              onChange={() => setIncludedApps(allIncluded ? new Set() : new Set(selectedApps.map((a: any) => a.application_id)))}
              style={{ accentColor: '#16a34a', flexShrink: 0 }} />
            <span style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '.04em' }}>
              📎 Applications to analyze — {includedApps.size} of {selectedApps.length}
            </span>
          </div>
          {selectedApps.length === 0 ? <span style={{ fontSize: 12, color: '#94a3b8' }}>No applications selected.</span> :
            /* Wrapped, not stacked. Twenty-one applications one-per-row consumed the
               whole top of the panel and pushed the instructions editor — the thing you
               are actually here to write — into a sliver. Side by side they take three
               rows instead of twenty-one. */
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {selectedApps.map((a) => {
                const on = includedApps.has(a.application_id);
                return (
                  <label key={a.application_id}
                    title={`${a.application_id_code}  ${a.application_name}${a.vendor_family ? '  ·  ' + a.vendor_family : ''}${a.analysed ? '  ·  re-analysis' : ''}\n${on ? 'Untick to leave this application out of the run' : 'Not included in this run'}`}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, padding: '3px 8px', border: '1px solid', borderColor: on ? '#bbf7d0' : '#e2e8f0', cursor: running ? 'default' : 'pointer', background: on ? '#f0fdf4' : '#fff', opacity: on ? 1 : 0.55, maxWidth: 260, whiteSpace: 'nowrap' }}>
                    <input type="checkbox" checked={on} disabled={running}
                      onChange={() => toggleApp(a.application_id)} style={{ accentColor: '#16a34a', flexShrink: 0 }} />
                    <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontWeight: 700, color: '#111827', flexShrink: 0 }}>{a.application_id_code}</span>
                    {/* The name is the part that ellipses. The ID is what people match
                        against, the vendor repeats down the whole list, and the full text
                        is on the label's title. */}
                    <span style={{ color: '#334155', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.application_name}</span>
                    {a.analysed && <span style={{ color: '#b45309', flexShrink: 0 }}>·re</span>}
                  </label>
                );
              })}
            </div>
          }
          <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 8 }}>
            {/* Contract coverage is a property of the run now, not of each application —
                one document from the library grounds every application in the run. */}
            {docIds.length > 0
              ? <>Grounded in <strong style={{ color: '#166534' }}>{docIds.length} contract document{docIds.length === 1 ? '' : 's'}</strong>, applied to all {includedApps.size}.</>
              : <><strong style={{ color: '#b45309' }}>No contract document selected</strong> — pick one below or the result is an estimate.</>}
            {appliedPreview.length > 0 && <> · Templates applied: {appliedPreview.map((t, i) => <span key={i}>{i > 0 ? ' · ' : ''}<strong style={{ color: '#4338ca' }}>{t.name}</strong> ({t.n})</span>)}</>}
            {reruns > 0 && <> · <strong style={{ color: '#b45309' }}>{reruns} already analysed</strong> — you'll confirm an additional analysis on Execute.</>}
          </div>
        </div>

        {/* two panes */}
        <div style={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>
          {/* left: prompt library */}
          <div style={{ width: 280, flexShrink: 0, borderRight: '1px solid #eef2f7', minHeight: 0, background: '#fcfdff', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '10px 14px 6px', flexShrink: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '.04em' }}>Prompt library</div>
              {deleteMode && <div style={{ fontSize: 11.5, color: '#b91c1c', marginTop: 3 }}>Tick the prompt(s) to delete, then confirm below.</div>}
            </div>
            <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
              {prompts.map((p, i) => {
                const isChecked = checked.has(p.id);
                return (
                  <div key={p.id} role="button" onClick={() => deleteMode ? toggleChecked(p.id) : pick(p)} style={{
                    width: '100%', textAlign: 'left', padding: '10px 14px', borderTop: '1px solid #f1f5f9',
                    background: deleteMode ? (isChecked ? '#fef2f2' : 'transparent') : (p.id === selectedId ? '#eef2ff' : 'transparent'), cursor: 'pointer',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {deleteMode && <input type="checkbox" readOnly checked={isChecked} style={{ pointerEvents: 'none', marginRight: 2, flexShrink: 0, accentColor: '#dc2626' }} />}
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#111827', flexShrink: 0 }}>{i + 1}.</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#111827', minWidth: 0, flex: 1, overflowWrap: 'anywhere' }}>{p.name}</span>
                      {!deleteMode && (
                        <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', flexDirection: 'column', flexShrink: 0, gap: 1 }}>
                          <button onClick={() => movePrompt(i, -1)} disabled={busy || i === 0} title="Move up" style={{ ...arrowBtn, opacity: (busy || i === 0) ? 0.35 : 1 }}>▲</button>
                          <button onClick={() => movePrompt(i, 1)} disabled={busy || i === prompts.length - 1} title="Move down" style={{ ...arrowBtn, opacity: (busy || i === prompts.length - 1) ? 0.35 : 1 }}>▼</button>
                        </span>
                      )}
                    </div>
                    {p.description && <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 3, lineHeight: 1.35, overflowWrap: 'anywhere' }}>{p.description}</div>}
                  </div>
                );
              })}
            </div>
            <div style={{ padding: 10, borderTop: '1px solid #eef2f7', flexShrink: 0, display: 'flex', gap: 8 }}>
              {deleteMode ? (
                <>
                  <button onClick={exitDeleteMode} disabled={busy} style={{ ...toolBtn, flex: 1, whiteSpace: 'nowrap' }}>Cancel</button>
                  <button onClick={onBulkDelete} disabled={busy || checked.size === 0} title="Delete the checked prompt(s)"
                    style={{ ...toolBtn, flex: 1, whiteSpace: 'nowrap', background: '#fef2f2', borderColor: '#fecaca', color: '#b91c1c', opacity: checked.size === 0 ? 0.5 : 1 }}>🗑 Delete ({checked.size})</button>
                </>
              ) : (
                <>
                  <button onClick={() => { if (isAdmin) openGenerate(); }} disabled={busy || !isAdmin}
                    title={isAdmin ? 'Author a new prompt (AI-generated) for the selected application(s)' : 'Only Administrators can create prompts'}
                    style={{ ...toolBtn, flex: 1, whiteSpace: 'nowrap', background: '#eef2ff', borderColor: '#c7d2fe', color: '#4338ca', opacity: isAdmin ? 1 : 0.5, cursor: isAdmin ? 'pointer' : 'not-allowed' }}>＋ New Prompt</button>
                  <button onClick={() => { if (isAdmin) { setChecked(new Set()); setDeleteMode(true); } }} disabled={busy || !isAdmin}
                    title={isAdmin ? 'Select prompt(s) to delete' : 'Only Administrators can delete prompts'}
                    style={{ ...toolBtn, flex: 1, whiteSpace: 'nowrap', background: '#fef2f2', borderColor: '#fecaca', color: '#b91c1c', opacity: isAdmin ? 1 : 0.5, cursor: isAdmin ? 'pointer' : 'not-allowed' }}>🗑 Delete</button>
                </>
              )}
            </div>
          </div>

          {/* right: editor */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
            <div style={{ padding: '12px 16px 8px', flexShrink: 0 }}>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Template name"
                style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, fontWeight: 600 }} />
              {/* Applications this prompt is dedicated to (drives run-time routing) */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                <span style={{ fontSize: 10.5, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '.04em' }}>Dedicated to</span>
                {linkedAppIds.length === 0
                  ? <span style={{ fontSize: 11.5, color: '#94a3b8' }}>vendor-family routing (no specific apps)</span>
                  : linkedAppIds.map((aid) => {
                      const app = (apps || []).find((x) => x.id === aid);
                      return <span key={aid} title={app?.name} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 12, background: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe', fontFamily: 'ui-monospace, Menlo, monospace' }}>{app ? (app.applicationIdCode || app.name) : aid.slice(0, 8)}</span>;
                    })}
              </div>
            </div>

            {/* interactive configuration (§1) — only for vendor modules that define a schema */}
            {schema.length > 0 && (
              /* Capped well below its old 320px. The knobs are set once and then left
                 alone; the instructions are read and rewritten every run, so the space
                 belongs to the editor. This block scrolls if it needs to. */
              <div style={{ flexShrink: 0, margin: '0 16px 8px', border: '1px solid #e5e7eb', borderRadius: 10, background: '#fafbff', maxHeight: 260, overflowY: 'auto' }}>
                <div style={{ padding: '8px 12px', fontSize: 11, fontWeight: 700, color: '#4338ca', textTransform: 'uppercase', letterSpacing: '.04em', position: 'sticky', top: 0, background: '#fafbff', borderBottom: '1px solid #eef2f7' }}>Configuration — these knobs shape the analysis</div>
                {/* DROPDOWNS in a responsive grid, not a row of pills per knob (owner's
                    ruling, 2026-08-04, carried over from the SPN Discoveries prototype).
                    Two things improve.

                    SIZE: eight knobs at four or five values each is around forty buttons
                    that wrap differently at every pane width. Eight selects are eight
                    controls of fixed height, so the block stops fighting the editor for
                    room and the grid reflows cleanly instead of re-wrapping pills.

                    THE HINT BECOMES VISIBLE. It was behind a `title` on a small ⓘ, which
                    means it only existed for someone who hovered exactly there — and the
                    hint is what tells you what the knob actually changes. It now sits
                    under its control and is simply read. */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 12, padding: 12 }}>
                  {schema.map((f: any) => (
                    <div key={f.key}>
                      <label
                        htmlFor={`cfg-${f.key}`}
                        style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: '#64748b', marginBottom: 4 }}
                      >
                        {f.label}
                      </label>
                      <select
                        id={`cfg-${f.key}`}
                        value={config[f.key] ?? f.default ?? ''}
                        onChange={(e) => setConfig((c) => ({ ...c, [f.key]: e.target.value }))}
                        style={{ width: '100%', font: 'inherit', fontSize: 13, padding: '7px 9px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', color: '#0f172a', cursor: 'pointer' }}
                      >
                        {f.values.map((v: string) => (
                          <option key={v} value={v}>
                            {v}
                          </option>
                        ))}
                      </select>
                      {f.hint ? (
                        <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 4 }}>{f.hint}</div>
                      ) : null}
                    </div>
                  ))}
                </div>
                <div style={{ padding: '0 12px 10px' }}>
                  <div style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11, color: '#4338ca', background: '#eef2ff', border: '1px solid #e0e7ff', borderRadius: 8, padding: '6px 9px', overflowWrap: 'anywhere' }}>{runConfigLine(schema, config)}</div>
                </div>
              </div>
            )}

            {/* The editor takes what is left, and never less than 300px — it was ending
                up around a hundred, which is four visible lines of a prompt that runs to
                dozens. */}
            <div style={{ flex: 1, display: 'flex', padding: '0 16px', minHeight: 300 }}>
              <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Negotiation instructions Claude should follow for this run…"
                wrap="soft"
                style={{ width: '100%', height: '100%', minHeight: 300, boxSizing: 'border-box', padding: '12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, lineHeight: 1.5, fontFamily: 'inherit', resize: 'none', overflowY: 'auto', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', wordBreak: 'break-word' }} />
            </div>
            {/* toolbar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderTop: '1px solid #eef2f7', flexShrink: 0 }}>
              <button onClick={onCopy} disabled={busy} style={toolBtn}>Copy</button>
              <button onClick={onSave} disabled={busy} style={{ ...toolBtn, background: '#eef2ff', borderColor: '#c7d2fe', color: '#4338ca' }}>Save</button>
              <button onClick={onReset} disabled={busy || !selectedId} style={toolBtn}>Reset to default</button>
              {isDraft && <span style={{ fontSize: 11.5, color: '#b45309', fontWeight: 700 }}>Unsaved draft</span>}
              {flash && <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 600 }}>{flash}</span>}
            </div>
          </div>

          {/* THIRD PANE — the contract documents.
              They were a 108px-tall strip in the footer, which is workable for two files
              and unusable for thirty: no search, and a scroll you had to hunt in. Their
              own column, full height, with a filter and the selected ones held at the top
              so a choice made can still be seen after scrolling past it. */}
          <div style={{ width: 320, flexShrink: 0, borderLeft: '1px solid #eef2f7', minHeight: 0, background: '#fcfdff', display: 'flex', flexDirection: 'column' }}>
            {/* GROUPING — top section of this column, above the documents.
                It was a full-width band under the panel title, where it read as part of the
                header and went unnoticed. Here it sits directly above the documents it
                belongs with: both answer "what is this run made of". Fixed, like the
                document heading below it — only the document LIST scrolls. */}
            <div style={{ flexShrink: 0, padding: '10px 14px', borderBottom: '1px solid #eef2f7' }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: '#111827', textTransform: 'uppercase', letterSpacing: '.05em' }}>Group analysis by</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 7 }}>
                {([
                  ['vendor', 'Vendor family', 'One strategy per vendor — the whole Microsoft estate negotiates as a single block. Combined volume is the leverage.'],
                  ['parent', 'Parent tree', 'One strategy per parent and per standalone, mirroring Application Master Data. Splits a vendor across several negotiations.'],
                ] as const).map(([value, labelText, hint]) => (
                  <label key={value} title={hint}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, cursor: running ? 'default' : 'pointer', color: grouping === value ? '#111827' : '#6b7280', fontWeight: grouping === value ? 700 : 400 }}>
                    <input type="radio" name="lic-grouping" value={value} checked={grouping === value} disabled={running}
                      onChange={() => onGroupingChange(value)} />
                    {labelText}
                  </label>
                ))}
              </div>
              {/* The choice stated in words. A filled radio says which one; this says what
                  it will DO, which is the part that was being missed. */}
              <div style={{ fontSize: 11, color: '#4b5563', marginTop: 6, lineHeight: 1.4 }}>
                {grouping === 'vendor'
                  ? 'One strategy per vendor — the whole estate negotiates as one block.'
                  : 'One strategy per parent and per standalone application.'}
              </div>
            </div>

            <div style={{ padding: '10px 14px 8px', flexShrink: 0, borderBottom: '1px solid #eef2f7' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                Contract documents
                {docIds.length > 0 && <span style={{ color: '#4338ca' }}> — {docIds.length} selected</span>}
              </div>
              <input
                value={docFilter}
                onChange={(e) => setDocFilter(e.target.value)}
                placeholder="Filter by file name…"
                style={{ width: '100%', boxSizing: 'border-box', marginTop: 7, height: 28, padding: '0 8px', border: '1px solid #cbd5e1', borderRadius: 0, fontSize: 12.5, fontFamily: 'inherit' }} />
              {/* No "Clear selection" button. Unticking the boxes already clears them, and
                  a control that duplicates what the checkbox does costs vertical space in
                  the one column where space is the whole point. */}
            </div>

            <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: '6px 10px 10px' }}>
              {libLoading ? (
                <div style={{ fontSize: 12, color: '#9ca3af', padding: '10px 4px' }}>Loading library…</div>
              ) : libDocs.length === 0 ? (
                <div style={{ fontSize: 12, color: '#b45309', padding: '10px 4px', lineHeight: 1.5 }}>
                  ⚠ No licensing documents in the library. Upload one under
                  <b> Knowledge Library · Licensing Documents</b>; the analysis runs without one but is an estimate.
                </div>
              ) : (() => {
                const q = docFilter.trim().toLowerCase();
                // Selected first, then the rest — a document ticked and then filtered out
                // of view looks like it was never ticked.
                const shown = libDocs
                  .filter((d: any) => !q || String(d.file_name || '').toLowerCase().includes(q) || String(d.description || '').toLowerCase().includes(q))
                  .sort((a: any, b: any) => Number(docIds.includes(b.id)) - Number(docIds.includes(a.id)));
                if (!shown.length) return <div style={{ fontSize: 12, color: '#9ca3af', padding: '10px 4px' }}>No document matches “{docFilter}”.</div>;
                return shown.map((d: any) => {
                  const on = docIds.includes(d.id);
                  return (
                    <label key={d.id} title={d.description || d.file_name}
                      style={{ display: 'flex', alignItems: 'flex-start', gap: 7, padding: '7px 8px', marginBottom: 4, border: '1px solid', borderColor: on ? '#c7d2fe' : '#eef2f7', background: on ? '#eef2ff' : '#fff', cursor: running ? 'default' : 'pointer' }}>
                      <input type="checkbox" checked={on} disabled={running} style={{ marginTop: 2, flexShrink: 0 }}
                        onChange={() => onDocIdsChange(on ? docIds.filter((x) => x !== d.id) : [...docIds, d.id])} />
                      <span style={{ minWidth: 0 }}>
                        <span style={{ display: 'block', fontSize: 12, color: on ? '#1e1b4b' : '#374151', fontWeight: on ? 700 : 500, overflowWrap: 'anywhere' }}>{d.file_name}</span>
                        <span style={{ display: 'block', fontSize: 10.5, color: '#94a3b8', marginTop: 2 }}>
                          {d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString() : ''}
                          {d.text_chars ? ` · ${Math.round(d.text_chars / 1000)}k chars` : ' · no text extracted'}
                        </span>
                      </span>
                    </label>
                  );
                });
              })()}
              {!libLoading && libDocs.length > 0 && docIds.length === 0 && (
                <div style={{ fontSize: 11.5, color: '#b45309', marginTop: 6, lineHeight: 1.45 }}>
                  ⚠ Nothing selected — the run falls back to documents attached to individual applications.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* execute footer */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '12px 18px', borderTop: '1px solid #e5e7eb', background: '#fff' }}>
          <div style={{ fontSize: 12, color: '#6b7280' }}>
            Each app's vendor template (+ its contract document) is sent to the analysis. Executing saves your changes first.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={btnGhost}>Cancel</button>
            <button onClick={onExecuteClick} disabled={running || includedApps.size === 0 || busy}
              title={includedApps.size === 0 ? 'Tick at least one application' : undefined}
              style={{ ...btnPrimary, opacity: (running || includedApps.size === 0 || busy) ? 0.5 : 1 }}>
              {running ? 'Running…' : `▶ Execute analysis (${includedApps.size})`}
            </button>
          </div>
        </div>
      </div>
    </div>
    {showGenerate && <GenerateModal apps={apps} initialVendor={selectedApps[0]?.vendor_name || ''} linkedAppIds={linkedAppIds} onGenerate={onGenerate} onClose={() => { setShowGenerate(false); setReplacePromptIds([]); }} busy={busy} />}
    </>
  );
};

// ── New-module generator modal (Administrator only) ──────────────────────────
const GenerateModal: React.FC<{ apps: any[] | null; initialVendor?: string; linkedAppIds: string[]; onGenerate: (inputs: { vendor: string; lifecycle_stage: string; commercial_model?: string; notes?: string }) => void; onClose: () => void; busy: boolean }> = ({ apps, initialVendor, linkedAppIds, onGenerate, onClose, busy }) => {
  const [vendor, setVendor] = useState(initialVendor || '');
  const [stage, setStage] = useState('');
  const [model, setModel] = useState('');
  const [notes, setNotes] = useState('');
  const canGo = !!vendor.trim() && !!stage.trim() && !busy;
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(560px, 94vw)', maxHeight: '90vh', overflowY: 'auto', background: '#fff', borderRadius: 14, boxShadow: '0 12px 40px rgba(16,24,40,.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid #e5e7eb' }}>
          <div><h2 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>New Prompt</h2><div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>AI-generates a vendor prompt from the authoring methodology and saves it; edit it afterwards.</div></div>
          <button onClick={onClose} title="Close" aria-label="Close" style={{ width: 36, height: 36, borderRadius: 9, border: '1.5px solid #dc2626', background: '#fee2e2', color: '#b91c1c', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><i className="material-icons" style={{ fontSize: 20 }}>close</i></button>
        </div>
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <label style={genLbl}>Vendor / product <span style={{ color: '#dc2626' }}>*</span>
            <input value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder="e.g. Adobe, ServiceNow, IBM…" style={genInput} />
          </label>
          <label style={genLbl}>Client lifecycle stage <span style={{ color: '#dc2626' }}>*</span>
            <select value={stage} onChange={(e) => setStage(e.target.value)} style={genInput}>
              <option value="">Select…</option>
              <option>Staying / optimizing the retained estate</option>
              <option>New purchase</option>
              <option>Migrating onto it</option>
              <option>Migrating off it</option>
              <option>Renewing</option>
              <option>Expanding</option>
            </select>
          </label>
          <label style={genLbl}>Commercial model (if known)
            <select value={model} onChange={(e) => setModel(e.target.value)} style={genInput}>
              <option value="">Unknown — let the model discover it</option>
              <option>perpetual + maintenance</option>
              <option>seat subscription</option>
              <option>consumption</option>
              <option>hybrid</option>
              <option>enterprise agreement</option>
            </select>
          </label>
          <label style={genLbl}>Notes / context (optional)
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Estate scale, imminent renewal / audit / end-of-life, anything relevant…" style={{ ...genInput, resize: 'vertical', fontFamily: 'inherit' }} />
          </label>
          <div style={{ padding: '10px 12px', background: '#f8fafc', border: '1px solid #eef2f7', borderRadius: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 6 }}>Generating for</div>
            {linkedAppIds.length === 0
              ? <span style={{ fontSize: 12, color: '#94a3b8' }}>No application selected.</span>
              : <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {linkedAppIds.map((aid) => { const a = (apps || []).find((x) => x.id === aid); return (
                    <span key={aid} title={a?.name} style={{ fontSize: 11.5, padding: '2px 9px', borderRadius: 12, background: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe' }}>
                      <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontWeight: 700 }}>{a?.applicationIdCode || aid.slice(0, 8)}</span> {a?.name || ''}
                    </span>
                  ); })}
                </div>}
            <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 6 }}>This prompt will be applied to the selected application(s) at run time.</div>
          </div>
          <div style={{ fontSize: 11.5, color: '#94a3b8' }}>Note: generation uses the model’s knowledge (no live web); every volatile fact is marked <code>[verify]</code> for you to confirm against the contract.</div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 18px', borderTop: '1px solid #e5e7eb' }}>
          <button onClick={onClose} style={btnGhost}>Cancel</button>
          <button onClick={() => onGenerate({ vendor: vendor.trim(), lifecycle_stage: stage, commercial_model: model || undefined, notes: notes || undefined })} disabled={!canGo} style={{ ...btnPrimary, opacity: canGo ? 1 : 0.5 }}>{busy ? 'Generating…' : '✨ Generate'}</button>
        </div>
      </div>
    </div>
  );
};

const genLbl: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: '#374151' };
const genInput: React.CSSProperties = { width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, fontWeight: 400, color: '#111827' };

const toolBtn: React.CSSProperties = { padding: '6px 12px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 12.5, cursor: 'pointer', fontWeight: 600 };
const arrowBtn: React.CSSProperties = { border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', borderRadius: 4, width: 18, height: 13, padding: 0, fontSize: 8, lineHeight: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const th: React.CSSProperties = { padding: '10px 12px', fontWeight: 600 };
const thR: React.CSSProperties = { ...th, textAlign: 'right' };
const td: React.CSSProperties = { padding: '9px 12px', verticalAlign: 'top' };
const tdR: React.CSSProperties = { ...td, textAlign: 'right' };
const h4: React.CSSProperties = { margin: '14px 0 4px', fontSize: 13, fontWeight: 700, color: '#374151' };
const p: React.CSSProperties = { margin: 0, fontSize: 13, lineHeight: 1.5, color: '#374151' };
const btnPrimary: React.CSSProperties = { padding: '8px 16px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' };
const btnGhost: React.CSSProperties = { padding: '6px 12px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 12, cursor: 'pointer' };

export default LicensingAnalysis;
